/**
 * Create Bug Report Hook
 *
 * Submits a bug report via GraphQL mutation.
 * Automatically reads organizationId from authStore_v1.
 */

import { useMutation } from '@tanstack/react-query';
import { axiosInstance } from '@/dataProvider/axios';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import toast from 'react-hot-toast';

const CREATE_BUG_REPORT_MUTATION = `
  mutation CreateBugReport($input: CreateBugReportInput!) {
    createBugReport(input: $input) {
      id
      organizationId
      reportedById
      summary
      description
      insertedAt
    }
  }
`;

interface CreateBugReportInput {
  organizationId: string;
  attachments: BugReportAttachment[];
  summary: string;
  description: string;
}

interface BugReportResponse {
  id: string;
  organizationId: string;
  reportedById: string;
  summary: string;
  description: string;
  insertedAt: string;
}

interface CreateBugReportData {
  createBugReport: BugReportResponse;
}

export interface BugReportAttachment {
  filename: string;
  content: string; // base64-encoded
}

export interface CreateBugReportParams {
  summary: string;
  description: string;
  attachments?: BugReportAttachment[];
}

/**
 * Hook to submit a bug report for the currently selected organization.
 *
 * @example
 * const { mutate, isPending } = useCreateBugReport();
 *
 * mutate({ summary: 'Login broken', description: 'Steps to reproduce...' });
 */
export function useCreateBugReport() {
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);

  return useMutation<BugReportResponse, Error, CreateBugReportParams>({
    mutationFn: async ({ summary, description, attachments }: CreateBugReportParams) => {
      if (!selectedOrg?.id) {
        throw new Error('No organization selected');
      }

      const input: CreateBugReportInput = {
        organizationId: selectedOrg.id,
        summary,
        description,
        attachments: attachments ?? [],
      };

      const response = await axiosInstance.post<CreateBugReportData>('/graphql', {
        query: CREATE_BUG_REPORT_MUTATION,
        variables: { input },
      });

      return response.data.createBugReport;
    },
    onSuccess: () => {
      toast.success("Bug report received. We'll look into it.");
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to submit bug report. Please try again.');
    },
  });
}
